import type { ExpandedState } from "@tanstack/react-table";

import { debounce } from "lodash";
import { create } from "zustand";

type UIState = {
  isFilterPanelVisible: boolean;
  toggleFilterPanel: () => void;
  userDropdownOpen: boolean;
  toggleUserDropdown: () => void;
  density: string;
  setDensity: (newDensity: string) => void;
  expandedRows: ExpandedState;
  setExpandedRows: (
    updater: ExpandedState | ((prevState: ExpandedState) => ExpandedState),
  ) => void;
  collapseAllRows: () => void;
  isTunecore: boolean;
  setIsTunecore: (isTunecore: boolean) => void;

  // Progress indicators for sidebar items
  progressItems: Set<string>;
  setProgressIndicator: (itemId: string, show: boolean) => void;
  hasProgressIndicator: (itemId: string) => boolean;
  clearAllProgressIndicators: () => void;
};

export const useUIStateStore = create<UIState>((set, get) => ({
  isFilterPanelVisible: true,
  toggleFilterPanel: () => set(state => ({ isFilterPanelVisible: !state.isFilterPanelVisible })),

  userDropdownOpen: false,
  toggleUserDropdown: () =>
    set(state => ({ userDropdownOpen: !state.userDropdownOpen })),

  density: localStorage.getItem("tableDensity") ?? "condensed",
  setDensity: debounce((newDensity: string) => {
    set({ density: newDensity });
    localStorage.setItem("tableDensity", newDensity);
  }, 100),

  expandedRows: {},
  setExpandedRows: updater =>
    set((state) => {
      const newExpandedRows
        = typeof updater === "function" ? updater(state.expandedRows) : updater;
      return { expandedRows: newExpandedRows };
    }),
  collapseAllRows: () => {
    set({ expandedRows: {} });
  },

  isTunecore: false,
  setIsTunecore: (isTunecore: boolean) => set({ isTunecore }),

  // Progress indicators
  progressItems: new Set<string>(),
  setProgressIndicator: (itemId: string, show: boolean) => {
    const currentItems = get().progressItems;
    const newItems = new Set(currentItems);

    if (show) {
      newItems.add(itemId);
    }
    else {
      newItems.delete(itemId);
    }

    set({ progressItems: newItems });
  },
  hasProgressIndicator: (itemId: string) => {
    return get().progressItems.has(itemId);
  },
  clearAllProgressIndicators: () => {
    set({ progressItems: new Set<string>() });
  },
}));
